import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { listSimulations } from "@/api/simulations";
import type { Simulation } from "@/types";

/**
 * Clé de cache canonique pour la liste des simulations de la session courante.
 *
 * Utilisée par `useSimulationsList` (lecture) et exposée pour permettre les
 * invalidations ciblées depuis d'autres écrans (ex. après création/run).
 */
export const SIMULATIONS_LIST_QUERY_KEY = ["simulations", "list"] as const;

/**
 * Intervalle de polling de la liste quand `refetchWhilePending` est actif
 * (Dashboard). Aligné sur l'ordre de grandeur du polling détail
 * (`SimulationResults`) : assez court pour que le passage `pending/running →
 * completed` soit perçu comme « instantané », sans marteler l'API.
 */
const SIMULATIONS_LIST_POLL_INTERVAL_MS = 3_000;

/**
 * Clé de cache canonique du **détail** d'une simulation (`GET /simulations/{id}`).
 *
 * Centralisée ici (S23-5) pour éviter la dérive : la page de résultats
 * (`SimulationResults`) et le wizard variante (`NewSimulation`) lisent déjà la
 * simulation sous `["simulations", "detail", id]`. Les mutations du cycle de
 * vie (edit / delete) doivent réconcilier **exactement** cette clé,
 * sinon un snapshot périmé persiste (leçon cache/identité 2026-06-04).
 */
export function simulationDetailQueryKey(
  id: string,
): readonly ["simulations", "detail", string] {
  return ["simulations", "detail", id] as const;
}

/**
 * Hook TanStack Query autour de `listSimulations()` (S3-4).
 *
 * Le backend renvoie déjà la liste triée par `created_at desc` ; on ne ré-trie
 * donc pas côté client. La pagination est volontairement absente du MVP : on
 * récupère jusqu'à `limit` (défaut backend = 50) entrées.
 *
 * `options.enabled` (H7-1) permet au Dashboard de différer la requête tant
 * que le consentement n'est pas acquis : sans gate, on tape `/simulations`
 * avant que `me.consent_given_at` soit posé, et le guard `require_consent`
 * répond 403 `CONSENT_REQUIRED` (visible bruyant dans la console).
 */
export function useSimulationsList(options?: {
  limit?: number;
  enabled?: boolean;
  /**
   * Dashboard (2026-07-02) — poll la liste tant qu'au moins une simulation est
   * `pending`/`running`, pour que le statut passe à `completed` (KPI/actions)
   * sans rafraîchir la page ; le polling s'arrête dès que tout est terminal.
   * Off par défaut : les autres consommateurs (ex. regroupement de variantes
   * dans `SimulationResults`) n'ont pas besoin de poller.
   */
  refetchWhilePending?: boolean;
}): UseQueryResult<Simulation[], unknown> {
  const limit = options?.limit;
  const enabled = options?.enabled ?? true;
  const refetchWhilePending = options?.refetchWhilePending ?? false;
  return useQuery({
    queryKey: limit !== undefined
      ? [...SIMULATIONS_LIST_QUERY_KEY, { limit }]
      : SIMULATIONS_LIST_QUERY_KEY,
    queryFn: () => listSimulations(limit),
    // Les simulations bougent peu (création manuelle) : pas besoin d'un
    // re-fetch agressif. 30s suffit à rester frais après une nav rapide.
    staleTime: 30_000,
    // …MAIS on **refetch systématiquement au montage** du dashboard : une simu
    // créée part en `pending` puis passe `completed` côté résultats (polling),
    // et une variante créée s'ajoute hors de cette vue. Sans ça, revenir au
    // dashboard dans la fenêtre `staleTime` réaffiche un snapshot périmé
    // (statut figé `pending`, variante non encore regroupée) jusqu'au refresh.
    // `"always"` garantit des données fraîches à chaque retour (refetch en
    // arrière-plan, le cache reste affiché le temps de la requête).
    refetchOnMount: "always",
    // Polling opt-in (Dashboard) : re-fetch toutes les 3s tant qu'au moins une
    // simulation est non terminale, puis `false` (arrêt) une fois tout
    // `completed`/`failed`. En arrière-plan désactivé pour ne pas poller un
    // onglet inactif.
    refetchInterval: refetchWhilePending
      ? (query) => {
          const sims = query.state.data;
          if (!sims) {
            return false;
          }
          const anyPending = sims.some(
            (s) => s.status === "pending" || s.status === "running",
          );
          return anyPending ? SIMULATIONS_LIST_POLL_INTERVAL_MS : false;
        }
      : undefined,
    refetchIntervalInBackground: false,
    enabled,
  });
}
